'use client';

import { useTranslations } from 'next-intl';
import { type KeyboardEvent, type ReactNode, useState } from 'react';

import { cn } from '@/lib/ui-kit/utils';
import { SearchInput } from '@/lib/ui-kit-v2/search-input';
import { MIN_SEARCH_LENGTH } from './core/constants';
import { rememberQuery } from './core/recentStore';
import { useSearchState } from './hooks/useSearchState';
import { SearchBody } from './SearchBody';

/** No pointer to follow on a phone: a row lights up while it is pressed. */
function renderPressableList(content: ReactNode) {
  return (
    <div className="pt-1 [&_[data-search-row]>a:active]:bg-v2-neutral-surface dark:[&_[data-search-row]>a:active]:bg-v2-surface-tertiary">
      {content}
    </div>
  );
}

interface MobileSearchPanelProps {
  onClose: () => void;
  className?: string;
}

/** The same search, full screen, for the bottom bar's Search on phones. */
export function MobileSearchPanel({ onClose, className }: MobileSearchPanelProps) {
  const [value, setValue] = useState('');
  const t = useTranslations('search');
  const search = useSearchState(value);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && value.trim().length >= MIN_SEARCH_LENGTH) rememberQuery(value);
  };

  return (
    // The dialog lays its content out on a grid, whose items grow to their
    // widest line; min-w-0 lets the rows truncate at the screen's edge.
    <div className={cn('flex w-full min-w-0 flex-col gap-4', className)}>
      <div onKeyDown={handleKeyDown}>
        <SearchInput value={value} onChange={setValue} placeholder={t('placeholder')} autoFocus />
      </div>
      {/* Rows and tabs keep their 8px inner padding, so their text lines up with the field's. */}
      <div className="-mx-2 flex min-w-0 flex-col">
        <SearchBody
          query={value}
          state={search}
          onQueryChange={setValue}
          onClose={onClose}
          renderList={renderPressableList}
        />
      </div>
    </div>
  );
}
